import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import BaseMapbox from '../../../../src/scripts/classes/BaseMapbox';

/**
 * MapPoi Component
 * Initializes and manages a Mapbox map with markers and popups
 * Handles clicks on both map markers and sidebar items
 * 
 * @constructor
 * @param {Object} options - Configuration options
 * @param {jQuery} options.$el - jQuery element containing the component
 * @param {string} options.baseClass - Base CSS class name
 * @param {string} options.accessToken - Mapbox access token
 * @param {string} options.style - Mapbox style URL
 * @param {Array<number>} options.center - Initial map center [lng, lat]
 * @param {number} options.zoom - Initial zoom level
 */
var MapPoi = function(options) {

	this.options = {
		$el: false,
		baseClass: 'mol-MapPoi',
		accessToken: 'pk.eyJ1IjoiYXV0aGVudGljZmYiLCJhIjoiUmtjWTY2byJ9.QeHZuWdaMRJlobq_RgXngw',
		style: 'mapbox://styles/authenticff/cmfzw19aj005e01s82akoh8mi',
		center: [-77.037855, 38.891725],
		zoom: 15.19
	};

	$.extend(true, this.options, options);

	this.map = null;
	this.$mapContainer = null;
	this.mapItems = [];
	this.markers = [];
	this.$sidebarItems = null;
	this.originalMapState = null;
	this.activeItemIndex = null;

};

/**
 * Initialize the map component
 * Sets up the Mapbox map, loads map items, and handles geocoding
 */
MapPoi.prototype.init = function() {

	var self = this;

	// Find the map container
	this.$mapContainer = this.options.$el.find('.map-poi-list_map');

	if (!this.$mapContainer.length) {
		console.warn('MapPoi: Map container not found');
		return;
	}

	// Get sidebar items
	this.$sidebarItems = this.options.$el.find('.map-poi-list_item');

	// Parse map items from data attribute
	this.mapItems = BaseMapbox.parseMapItems(this.options.$el, 'MapPoi');

	// Set access token
	mapboxgl.accessToken = this.options.accessToken;

	// Calculate initial center and zoom from items with coordinates
	var initialBounds = BaseMapbox.calculateInitialCenterAndZoom(
		this.mapItems,
		this.options.center,
		this.options.zoom
	);
	var initialCenter = initialBounds.center;
	var initialZoom = initialBounds.zoom;

	// Initialize the map (will fit bounds after all markers are added)
	this.map = new mapboxgl.Map({
		container: this.$mapContainer[0],
		style: this.options.style,
		center: initialCenter,
		zoom: initialZoom
	});

	// Setup map error handling
	BaseMapbox.setupMapErrorHandling(this.map, this.removeProblematicLayer.bind(this), 'MapPoi');

	// Add navigation controls (optional)
	this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');

	// Wait for map to load
	this.map.on('load', function() {
		// Ensure map fills container by triggering resize
		setTimeout(function() {
			self.map.resize();
		}, 100);
		
		if (self.mapItems && self.mapItems.length > 0) {
			self.geocodeAddressesAndAddMarkers();
		} else {
			console.warn('MapPoi: No map items to display');
		}
	});

	// Set up sidebar item click handlers
	this.setupSidebarClickHandlers();

};

/**
 * Remove a problematic layer from the map style
 * Wrapper for BaseMapbox.removeProblematicLayer
 * 
 * @param {string} layerId - The ID of the layer to remove
 */
MapPoi.prototype.removeProblematicLayer = function(layerId) {
	BaseMapbox.removeProblematicLayer(this.map, layerId, 'MapPoi');
};

/**
 * Geocode addresses and add markers to the map
 * Handles both items with existing coordinates and items that need geocoding
 * Fits map bounds after all markers are added
 */
MapPoi.prototype.geocodeAddressesAndAddMarkers = function() {

	var self = this;
	
	if (!this.mapItems || this.mapItems.length === 0) {
		return;
	}

	var geocodePromises = [];
	var itemsToGeocode = [];

	// Process items: add markers with coordinates, queue others for geocoding
	this.mapItems.forEach(function(item, index) {
		if (!item.address && !item.coordinates) {
			console.warn('MapPoi: Item missing address and coordinates', item);
			return;
		}

		// If valid coordinates already exist, add marker directly
		if (BaseMapbox.isValidCoordinate(item.coordinates)) {
			self.addMarker(item.coordinates, item, index);
		} else if (item.address) {
			// Need to geocode
			itemsToGeocode.push({ item: item, index: index });
		}
	});

	// If all items already have coordinates, fit bounds after a brief delay
	if (itemsToGeocode.length === 0) {
		if (this.markers.length > 0) {
			setTimeout(function() {
				self.fitMapToMarkers();
			}, 100);
		}
		return;
	}

	// Geocode each address that needs it
	itemsToGeocode.forEach(function(itemData) {
		var item = itemData.item;
		var index = itemData.index;

		var geocodeUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + 
			encodeURIComponent(item.address) + 
			'.json?access_token=' + self.options.accessToken;

		var promise = $.ajax({
			url: geocodeUrl,
			method: 'GET'
		}).done(function(response) {
			if (response.features && response.features.length > 0) {
				var coordinates = response.features[0].center; // [lng, lat]
				
				// Store coordinates in the item
				self.mapItems[index].coordinates = coordinates;
				
				// Add marker to map
				self.addMarker(coordinates, item, index);
			} else {
				console.warn('MapPoi: No results for address', item.address);
			}
		}).fail(function(xhr, status, error) {
			console.error('MapPoi: Geocoding error for', item.address, error);
		});

		geocodePromises.push(promise);
	});

	// Wait for all geocoding to complete, then fit bounds
	$.when.apply($, geocodePromises).then(function() {
		if (self.markers.length > 0) {
			setTimeout(function() {
				self.fitMapToMarkers();
			}, 100);
		} else {
			console.warn('MapPoi: No markers were successfully added');
		}
	});

};

/**
 * Add a marker to the map with a popup
 * 
 * @param {Array<number>} coordinates - Map coordinates [lng, lat]
 * @param {Object} item - The map item data containing popup information
 * @param {number} itemIndex - The index of the item in mapItems array
 */
MapPoi.prototype.addMarker = function(coordinates, item, itemIndex) {

	var self = this;

	// Create marker element.
	// Highlighted places (place_highlight) stand out from standard markers — typically
	// the property itself among nearby points of interest.
	var isHighlight = !!item.highlight;

	var el = document.createElement('div');
	el.className = 'map-poi-list_marker' + (isHighlight ? ' map-poi-list_marker--highlight' : '');
	el.style.width = isHighlight ? '40px' : '30px';
	el.style.height = isHighlight ? '40px' : '30px';
	el.style.borderRadius = '50%';
	el.style.cursor = 'pointer';

	// Highlighted markers take their fill from the --amp-poi-highlight custom property
	// (see MapPoi.scss) unless the CMS supplies an explicit place_highlightColor.
	// Only set an inline background when we must, since inline beats the class.
	if (!isHighlight) {
		el.style.backgroundColor = '#000';
	} else if (item.highlightColor) {
		el.style.backgroundColor = item.highlightColor;
	}

	// Create and add marker to map
	var marker = new mapboxgl.Marker(el)
		.setLngLat(coordinates)
		.addTo(this.map);

	// Use pre-rendered popup HTML (should always be defined)
	var popupContent = item.popupHtml || '';
	
	if (!popupContent) {
		console.warn('MapPoi: No popupHtml defined for item', item);
		return;
	}

	// Create and attach popup
	var popup = new mapboxgl.Popup({
		offset: 0,
		closeButton: false,
		// Prevents focus moving to popup links (and browser scrollIntoView), which caused intermittent page scroll jumps when selecting sidebar items (see mapbox-gl-js#10329).
		focusAfterOpen: false
	})
	.setHTML(popupContent);

	marker.setPopup(popup);

	// Store marker reference with item index for sidebar linking
	this.markers.push({
		marker: marker,
		itemIndex: itemIndex,
		coordinates: coordinates
	});

	// Add click handler to marker
	el.addEventListener('click', function() {
		self.openPopupForItem(itemIndex);
	});

};

/**
 * Fit map bounds to show all markers
 * Centers the map view to include all markers with appropriate padding
 * Handles single marker case by centering with a fixed zoom level
 */
MapPoi.prototype.fitMapToMarkers = function() {

	if (this.markers.length === 0) {
		console.warn('MapPoi: No markers to fit bounds to');
		return;
	}

	// Extract coordinates from markers
	var coordinates = this.markers.map(function(markerData) {
		return markerData.coordinates;
	});

	// Use BaseMapbox utility to fit bounds
	BaseMapbox.fitMapBounds(this.map, coordinates, {
		padding: { top: 100, bottom: 100, left: 100, right: 100 },
		maxZoom: 15,
		duration: 1000,
		singleMarkerZoom: 14
	});

	// Capture the fitted view as the baseline for reset-on-second-click
	var self = this;
	var captureState = function() {
		self.storeOriginalMapState();
	};
	this.map.once('moveend', captureState);
	setTimeout(captureState, 1100);

};

/**
 * Store the current map view as the original presentation state
 * Only captured once after the initial fit-to-markers animation
 */
MapPoi.prototype.storeOriginalMapState = function() {

	if (this.originalMapState) {
		return;
	}

	this.originalMapState = {
		center: this.map.getCenter(),
		zoom: this.map.getZoom(),
		bearing: this.map.getBearing(),
		pitch: this.map.getPitch()
	};

};

/**
 * Reset map to its original zoom, center, and orientation
 * Closes popups and clears sidebar active state
 */
MapPoi.prototype.resetMapToOriginalState = function() {

	// Close all open popups
	this.markers.forEach(function(m) {
		var popup = m.marker.getPopup();
		if (popup) {
			popup.remove();
		}
	});
	var mapContainer = this.map.getContainer();
	var popupEls = mapContainer.querySelectorAll('.mapboxgl-popup');
	for (var i = 0; i < popupEls.length; i++) {
		popupEls[i].remove();
	}

	this.$sidebarItems.removeClass('is-active');
	this.activeItemIndex = null;

	if (this.originalMapState) {
		this.map.flyTo({
			center: this.originalMapState.center,
			zoom: this.originalMapState.zoom,
			bearing: this.originalMapState.bearing,
			pitch: this.originalMapState.pitch,
			duration: 1000
		});
	} else {
		this.fitMapToMarkers();
	}

};

/**
 * Set up click handlers for sidebar items
 * Uses event delegation so clicks on child elements (e.g. text) still work
 */
MapPoi.prototype.setupSidebarClickHandlers = function() {

	var self = this;

	this.options.$el.on('click', '.map-poi-list_item', function(e) {
		e.preventDefault();
		var $item = $(e.currentTarget);
		var mapItemId = $item.attr('data-map-item-id');

		if (mapItemId !== undefined && mapItemId !== null && mapItemId !== '') {
			var itemIndex = parseInt(mapItemId, 10);
			if (!isNaN(itemIndex)) {
				self.openPopupForItem(itemIndex);
			}
		}
	});

};

/**
 * Open popup for a specific item and recenter map
 * 
 * @param {number} itemIndex - The index of the item in mapItems array
 */
MapPoi.prototype.openPopupForItem = function(itemIndex) {

	if (itemIndex < 0 || itemIndex >= this.mapItems.length) {
		console.warn('MapPoi: Invalid item index', itemIndex);
		return;
	}

	// Second click on the active item resets the map to its original view
	if (this.activeItemIndex === itemIndex) {
		this.resetMapToOriginalState();
		return;
	}

	// Find the marker with matching itemIndex
	var markerData = null;
	for (var i = 0; i < this.markers.length; i++) {
		if (this.markers[i].itemIndex === itemIndex) {
			markerData = this.markers[i];
			break;
		}
	}

	if (!markerData) {
		console.warn('MapPoi: Marker not found for item index', itemIndex);
		return;
	}

	// Close all open popups - use both API and DOM to ensure they're fully removed
	this.markers.forEach(function(m) {
		var popup = m.marker.getPopup();
		if (popup) {
			popup.remove();
		}
	});
	// DOM fallback: remove any popup elements that may remain (e.g. from marker clicks)
	var mapContainer = this.map.getContainer();
	var popupEls = mapContainer.querySelectorAll('.mapboxgl-popup');
	for (var i = 0; i < popupEls.length; i++) {
		popupEls[i].remove();
	}

	// Open the popup for the selected item
	markerData.marker.togglePopup();

	// Recenter map on the marker
	this.map.flyTo({
		center: markerData.coordinates,
		zoom: 15,
		duration: 1000
	});

	// Highlight the sidebar item
	this.$sidebarItems.removeClass('is-active');
	var $sidebarItem = this.$sidebarItems.filter('[data-map-item-id="' + itemIndex + '"]');
	$sidebarItem.addClass('is-active');
	this.activeItemIndex = itemIndex;

};

export default MapPoi;
